import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { TextInput, CheckboxField } from '../../components/common/form';
import { getBillingSettings, saveBillingSettings } from '../../api/billingSettings';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { apiErrorMessage } from '../../utils/apiError';

function StatusChip({ on, t }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
        on ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
      }`}
    >
      {on ? t('settings.report.statusOn') : t('settings.report.statusOff')}
    </span>
  );
}

export default function BillingSettings() {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getBillingSettings();
        setForm({
          requirePaymentForAttendance: !!data.requirePaymentForAttendance,
          allowAttendanceWithUnpaidPeriod: data.allowAttendanceWithUnpaidPeriod !== false,
          allowAttendanceWithNegativeWallet: data.allowAttendanceWithNegativeWallet !== false,
          unpaidGraceCalendarDays: data.unpaidGraceCalendarDays ?? '',
          unpaidGraceSessionCount: data.unpaidGraceSessionCount ?? '',
          periodCollectionMaxDays: data.periodCollectionMaxDays ?? 4,
          enrollmentExpiryRemindersEnabled: data.enrollmentExpiryRemindersEnabled !== false,
        });
      } catch {
        setError(t('settings.billing.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const modeSummary = useMemo(() => {
    if (!form) return '';
    if (form.requirePaymentForAttendance) return t('settings.billing.modeStrict');
    if (form.allowAttendanceWithUnpaidPeriod) return t('settings.billing.modeGrace');
    return t('settings.billing.modeFlexible');
  }, [form, t]);

  const persist = useCallback(async () => {
    if (!form) return;
    await saveBillingSettings({
      requirePaymentForAttendance: form.requirePaymentForAttendance,
      allowAttendanceWithUnpaidPeriod: form.allowAttendanceWithUnpaidPeriod,
      allowAttendanceWithNegativeWallet: form.allowAttendanceWithNegativeWallet,
      unpaidGraceCalendarDays:
        form.unpaidGraceCalendarDays === '' ? null : Number(form.unpaidGraceCalendarDays),
      unpaidGraceSessionCount:
        form.unpaidGraceSessionCount === '' ? null : Number(form.unpaidGraceSessionCount),
      periodCollectionMaxDays: Number(form.periodCollectionMaxDays) || 4,
      enrollmentExpiryRemindersEnabled: form.enrollmentExpiryRemindersEnabled,
    });
  }, [form]);

  const { markSaved } = useUnsavedChanges(form, {
    enabled: !!form,
    onSave: persist,
  });

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      await persist();
      markSaved(form);
      setSuccess(t('settings.billing.saveSuccess'));
    } catch (err) {
      setError(apiErrorMessage(err, t('settings.billing.saveFailed')));
    } finally {
      setSaving(false);
    }
  }

  if (loading || !form) {
    if (error && !form) {
      return (
        <div className="page-shell-form">
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        </div>
      );
    }
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('settings.billing.title')}
        subtitle={t('settings.billing.subtitle')}
      />
    );
  }

  return (
    <div className="page-shell-form">
      <PageHeaderSection
        eyebrow={t('nav.feesBilling')}
        title={t('settings.billing.title')}
        subtitle={t('settings.billing.subtitle')}
      />

      <form onSubmit={handleSave} className="space-y-6">
        {success && (
          <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {success}
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Section 1 — attendance gating */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                  {t('settings.billing.stepOne')}
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                  {t('settings.billing.attendanceSection')}
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                  {t('settings.billing.attendanceSectionDesc')}
                </p>
              </div>
              <div className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                <p className="text-xs font-medium text-slate-500">{t('settings.billing.modeLabel')}</p>
                <p className="text-sm font-semibold text-slate-900">{modeSummary}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 px-5 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label
                className={`flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors ${
                  form.requirePaymentForAttendance
                    ? 'border-primary-200 bg-primary-50/40 hover:border-primary-300'
                    : 'border-slate-200 bg-slate-50/60 hover:border-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  checked={form.requirePaymentForAttendance}
                  onChange={(e) => setField('requirePaymentForAttendance', e.target.checked)}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">
                      {t('settings.billing.requirePayment')}
                    </span>
                    <StatusChip on={form.requirePaymentForAttendance} t={t} />
                  </div>
                  <p className="mt-1 text-sm leading-snug text-slate-600">
                    {t('settings.billing.requirePaymentDesc')}
                  </p>
                </div>
              </label>

              {!form.requirePaymentForAttendance && (
                <label
                  className={`flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors ${
                    form.allowAttendanceWithUnpaidPeriod
                      ? 'border-primary-200 bg-primary-50/40 hover:border-primary-300'
                      : 'border-slate-200 bg-slate-50/60 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    checked={form.allowAttendanceWithUnpaidPeriod}
                    onChange={(e) => setField('allowAttendanceWithUnpaidPeriod', e.target.checked)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">
                        {t('settings.billing.allowUnpaidPeriod')}
                      </span>
                      <StatusChip on={form.allowAttendanceWithUnpaidPeriod} t={t} />
                    </div>
                    <p className="mt-1 text-sm leading-snug text-slate-600">
                      {t('settings.billing.allowUnpaidPeriodDesc')}
                    </p>
                  </div>
                </label>
              )}
            </div>

            {!form.requirePaymentForAttendance && form.allowAttendanceWithUnpaidPeriod && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                <p className="mb-3 text-sm font-semibold text-slate-800">
                  {t('settings.billing.graceSection')}
                </p>
                <p className="mb-4 text-sm text-slate-600">{t('settings.billing.graceSectionDesc')}</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <TextInput
                    label={t('settings.billing.graceCalendarDays')}
                    name="unpaidGraceCalendarDays"
                    type="number"
                    min="0"
                    note={t('settings.billing.graceOptionalNote')}
                    value={form.unpaidGraceCalendarDays}
                    onChange={(e) => setField('unpaidGraceCalendarDays', e.target.value)}
                  />
                  <TextInput
                    label={t('settings.billing.graceSessions')}
                    name="unpaidGraceSessionCount"
                    type="number"
                    min="0"
                    note={t('settings.billing.graceOptionalNote')}
                    value={form.unpaidGraceSessionCount}
                    onChange={(e) => setField('unpaidGraceSessionCount', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Section 2 — wallet & collection */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
              {t('settings.billing.stepTwo')}
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">
              {t('settings.billing.walletSection')}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              {t('settings.billing.walletSectionDesc')}
            </p>
          </div>
          <div className="space-y-4 px-5 py-5 sm:px-6">
            <CheckboxField
              layout="toggle-card"
              name="allowAttendanceWithNegativeWallet"
              label={t('settings.billing.allowNegativeWallet')}
              description={t('settings.billing.allowNegativeWalletDesc')}
              checked={form.allowAttendanceWithNegativeWallet}
              onChange={(e) => setField('allowAttendanceWithNegativeWallet', e.target.checked)}
            />
            <div className="max-w-md">
              <TextInput
                label={t('settings.billing.collectionWindow')}
                name="periodCollectionMaxDays"
                type="number"
                min="1"
                max="14"
                note={t('settings.billing.collectionWindowDesc')}
                value={form.periodCollectionMaxDays}
                onChange={(e) => setField('periodCollectionMaxDays', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Section 3 — reminders */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
              {t('settings.billing.stepThree')}
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">
              {t('settings.billing.remindersSection')}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              {t('settings.billing.remindersSectionDesc')}
            </p>
          </div>
          <div className="px-5 py-5 sm:px-6">
            <CheckboxField
              layout="toggle-card"
              name="enrollmentExpiryRemindersEnabled"
              label={t('settings.billing.expiryReminders')}
              description={t('settings.billing.expiryRemindersDesc')}
              checked={form.enrollmentExpiryRemindersEnabled}
              onChange={(e) => setField('enrollmentExpiryRemindersEnabled', e.target.checked)}
            />
          </div>
        </section>

        <div className="sticky bottom-4 z-10 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? t('forms.common.saving') : t('settings.billing.saveSettings')}
          </button>
        </div>
      </form>
    </div>
  );
}
